// src/components/landing/StudyModesBlock.tsx
// Bloco 02 — Modos de Estudo | GRAIA Landing
// Prefixo CSS: smdb-

import React, { useState, useEffect, useRef } from 'react';
import { Layers, Timer, RefreshCw } from 'lucide-react';
import './StudyModesBlock.css';

type ModeId = 'free' | 'focus' | 'review';

interface StudyMode {
    id: ModeId;
    icon: React.ElementType;
    label: string;
    title: string;
    description: string;
}

const MODES: StudyMode[] = [
    {
        id: 'free',
        icon: Layers,
        label: 'Modo Livre',
        title: 'Modo Livre',
        description: 'Estude com liberdade total. Sem limite de tempo. Sem restrições.',
    },
    {
        id: 'focus',
        icon: Timer,
        label: 'Modo Foco',
        title: 'Modo Foco',
        description: 'Sessão estruturada de concentração profunda. Tempo definido. Ambiente isolado.',
    },
    {
        id: 'review',
        icon: RefreshCw,
        label: 'Modo Revisão',
        title: 'Modo Revisão',
        description: 'Reúne apenas os cards avaliados como difíceis. Revisão direcionada.',
    },
];

const StudyModesBlock: React.FC = () => {
    const sectionRef = useRef<HTMLElement>(null);

    const [sectionVisible, setSectionVisible] = useState(false);
    const [selectedMode, setSelectedMode] = useState<ModeId>('review');
    const [reviewCount, setReviewCount] = useState(12);
    const [decremented, setDecremented] = useState(false);
    const [countTransitioning, setCountTransitioning] = useState(false);

    // IntersectionObserver — dispara a coreografia CSS
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setSectionVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.12 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    const handleSelectMode = (id: ModeId) => {
        setSelectedMode(id);
        if (id === 'review' && !decremented) {
            setCountTransitioning(true);
            setTimeout(() => {
                setReviewCount(prev => Math.max(0, prev - 1));
                setCountTransitioning(false);
                setDecremented(true);
            }, 150);
        }
    };

    return (
        <section
            className={`smdb-section ${sectionVisible ? 'smdb-section--visible' : ''}`}
            ref={sectionRef}
        >
            <div className="smdb-container">
                <div className="smdb-grid">

                    {/* ─── COLUNA ESQUERDA ─── */}
                    <div className="smdb-left">
                        <p className="smdb-label">Modos de Estudo</p>

                        <h2 className="smdb-headline">
                            Estratégias diferentes<br />para momentos diferentes.
                        </h2>

                        <p className="smdb-body">
                            O algoritmo organiza o que deve ser estudado. Você define como quer executar a sessão.
                            Três arquiteturas estruturam sua prática de acordo com seu estado cognitivo.
                        </p>

                        <p className="smdb-closing">
                            Automação quando faz sentido.<br />
                            <strong>Controle quando é necessário.</strong>
                        </p>
                    </div>

                    {/* ─── COLUNA DIREITA: MODAL ─── */}
                    <div className="smdb-right">
                        <div className="smdb-modal">

                            <div className="smdb-modal-header">
                                <div className="smdb-modal-header-left">
                                    <span className="smdb-modal-logo">GRAIA</span>
                                    <span className="smdb-modal-sep" />
                                    <span className="smdb-modal-title-text">Iniciar Sessão</span>
                                </div>
                                <button className="smdb-modal-close" aria-label="Fechar">
                                    <span>✕</span>
                                </button>
                            </div>

                            <div className="smdb-modes-list">
                                {MODES.map((mode) => {
                                    const Icon = mode.icon;
                                    const isSelected = selectedMode === mode.id;

                                    return (
                                        <button
                                            key={mode.id}
                                            className={[
                                                'smdb-mode-item',
                                                isSelected ? 'smdb-mode-item--selected' : '',
                                            ].filter(Boolean).join(' ')}
                                            onClick={() => handleSelectMode(mode.id)}
                                        >
                                            <div className="smdb-mode-icon">
                                                <Icon size={16} strokeWidth={1.75} />
                                            </div>

                                            <div className="smdb-mode-body">
                                                <div className="smdb-mode-name-row">
                                                    <span className="smdb-mode-name">{mode.title}</span>
                                                    {mode.id === 'review' && (
                                                        <span className={`smdb-review-badge ${countTransitioning ? 'smdb-review-badge--transitioning' : ''}`}>
                                                            {reviewCount} cards
                                                        </span>
                                                    )}
                                                    {mode.id === 'focus' && (
                                                        <span className="smdb-focus-badge">20 min</span>
                                                    )}
                                                </div>
                                                <p className="smdb-mode-desc">{mode.description}</p>
                                            </div>

                                            <div className={`smdb-mode-radio ${isSelected ? 'smdb-mode-radio--selected' : ''}`} />
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="smdb-modal-footer">
                                <button
                                    className="smdb-modal-btn-cancel"
                                    onClick={() => setSelectedMode('free')}
                                >
                                    Cancelar
                                </button>
                                <button
                                    className="smdb-modal-btn-start"
                                    onClick={() => handleSelectMode('review')}
                                >
                                    {selectedMode === 'review'
                                        ? `Iniciar Revisão (${reviewCount})`
                                        : `Iniciar ${MODES.find(m => m.id === selectedMode)?.label}`
                                    }
                                </button>
                            </div>

                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default StudyModesBlock;